import React, { useCallback, useMemo } from 'react';
import classNames from 'classnames';
import { useIntl } from 'umi';
import { Button, Form } from 'tea-component';

import TagFilterSelect from '@/pages/datalab/components/TagFilterSelect';
import { WizardStep } from '../../constant';
import type { IFieldConfigRowEdit, IStepFieldConfigProps } from '../../types';

import DimensionSection from './components/DimensionSection';
import MeasureSection from './components/MeasureSection';
import { useFieldConfigStepValidate } from './hooks/useFieldConfigStepValidate';
import { useFieldConfigTableColumns } from './hooks/useFieldConfigTableColumns';
import { useStepFieldConfigCore } from './hooks/useStepFieldConfigCore';
import { LANGUAGE_EN } from '@home/_config/config';
import { getLanguage } from '@home/utils/common';

import './index.scss';

/**
 * Step2「字段配置」页面壳组件。
 *
 * 职责：字段多选（参与 Cube 的列）、维度/指标两块表格与内联新增表单、
 * 底部上一步/下一步。列编辑与 `fieldsValue` 通过 `onChange` 写回父级状态。
 *
 * 「下一步」默认可点；点击后由 `useFieldConfigStepValidate` 聚合校验，
 * 未通过项展示 error 边框（顶部多选 / 表格单元格 / 维度·指标区块），
 * 全部通过才调用父级 `handleNext`（生成 YAML 并进入 Step3）。
 */
const StepFieldConfig: React.FC<IStepFieldConfigProps> = (props) => {
  const {
    setCurrent,
    value,
    onChange,
    handleNext,
    generateYamlLoading = false,
  } = props;
  const { locale, formatMessage } = useIntl();
  const isEn = locale === LANGUAGE_EN;
  const {
    fieldsValue,
    fieldColumns,
    dimInlineOpen,
    measInlineOpen,
    dimAddDraft,
    measAddDraft,
  } = value;

  const {
    isTableCellError,
    clearTableCellError,
    fieldsValueError,
    dimSecError,
    measSecError,
    validateOnNext,
    clearFieldsValueError,
    clearDimSecError,
    clearMeasSecError,
  } = useFieldConfigStepValidate();

  const changeDimInlineOpen = useCallback(
    (next: React.SetStateAction<boolean>) => {
      const resolved =
        typeof next === 'function' ? next(dimInlineOpen) : next;
      if (!resolved) {
        clearDimSecError();
      }
      onChange({ dimInlineOpen: resolved });
    },
    [dimInlineOpen, onChange, clearDimSecError],
  );

  const changeMeasInlineOpen = useCallback(
    (next: React.SetStateAction<boolean>) => {
      const resolved =
        typeof next === 'function' ? next(measInlineOpen) : next;
      if (!resolved) {
        clearMeasSecError();
      }
      onChange({ measInlineOpen: resolved });
    },
    [measInlineOpen, onChange, clearMeasSecError],
  );

  const changeDimAddDraft = useCallback(
    (next: React.SetStateAction<IFieldConfigRowEdit>) => {
      const resolved =
        typeof next === 'function' ? next(dimAddDraft) : next;
      onChange({ dimAddDraft: resolved });
    },
    [dimAddDraft, onChange],
  );

  const changeMeasAddDraft = useCallback(
    (next: React.SetStateAction<IFieldConfigRowEdit>) => {
      const resolved =
        typeof next === 'function' ? next(measAddDraft) : next;
      onChange({ measAddDraft: resolved });
    },
    [measAddDraft, onChange],
  );

  /** 多选组件下拉选项：与 `fieldColumns` 同步，展示维度/指标 Tag + 字段名 */
  const fieldOptions = useMemo(
    () =>
      fieldColumns?.map((item) => {
        const isDimension = item.default_kind === 'dimension';
        const kindLabel = formatMessage({
          id: isDimension
            ? 'dbl_model_agent_step2_kind_dimension'
            : 'dbl_model_agent_step2_kind_metric',
        });
        return {
          value: item?.source_column,
          label: item?.source_column,
          text: item?.source_column,
          render: () => (
            <span className="dbl-field-config-field-option">
              <span
                className={classNames('dbl-field-config-kind-tag', {
                  'dbl-field-config-kind-tag--dim': isDimension,
                  'dbl-field-config-kind-tag--meas': !isDimension,
                  'dbl-field-config-kind-tag--min': isEn
                })}
              >
                {kindLabel}
              </span>
              {item.is_custom ? (
                <span className="dbl-field-config-kind-tag dbl-field-config-kind-tag--custom">
                  {formatMessage({ id: 'dbl_model_agent_step2_custom_tag' })}
                </span>
              ) : null}
              <span className="dbl-field-config-field-option__name">
                {getLanguage(isEn, item?.default_name_en, item?.default_name_zh) || item?.source_column || ''}
              </span>
            </span>
          ),
        };
      }),
    [fieldColumns, isEn, formatMessage],
  );

  /** 列 patch、行过滤、下拉 options、内联确认等（不含 Table 列 JSX） */
  const {
    patchColumn,
    handleFieldKindChange,
    dimRows,
    measRows,
    kindFieldOptions,
    dimTypeOptions,
    measTypeOptions,
    measFormatOptions,
    measGranularityOptions,
    handleConfirmDimInline,
    handleConfirmMeasInline,
  } = useStepFieldConfigCore({
    fieldColumns,
    fieldsValue,
    onChange,
    formatMessage,
    dimAddDraft,
    measAddDraft,
    changeDimInlineOpen,
    changeMeasInlineOpen,
  });

  /** 多选变更：更新参与 Cube 的字段 `source_column` 列表 */
  const handleFieldChange = (val: string[]): void => {
    onChange({ fieldsValue: val || [] });
  };

  const changeFieldsValue = useCallback(
    (next: string[]) => {
      onChange({ fieldsValue: next });
    },
    [onChange],
  );

  const allDimSourceColumns = useMemo(
    () =>
      fieldColumns
        .filter((c) => c.default_kind === 'dimension')
        .map((c) => c.source_column),
    [fieldColumns],
  );
  const allMeasSourceColumns = useMemo(
    () =>
      fieldColumns
        .filter((c) => c.default_kind === 'measure')
        .map((c) => c.source_column),
    [fieldColumns],
  );

  /** 维度表 / 指标表 Tea Table 列定义（含单元格内联编辑） */
  const { dimColumns, measColumns } = useFieldConfigTableColumns({
    formatMessage,
    fieldsValue,
    changeFieldsValue,
    allDimSourceColumns,
    allMeasSourceColumns,
    patchColumn,
    handleFieldKindChange,
    kindFieldOptions,
    dimTypeOptions,
    measTypeOptions,
    measFormatOptions,
    measGranularityOptions,
    isTableCellError,
    clearTableCellError,
  });

  /** 返回 Step1 */
  const handlePrev = (): void => {
    setCurrent(WizardStep.One);
  };

  /**
   * 先校验再进 Step3：不通过则写回 error 状态并停留本步；
   * 输入过程不高亮，与内联「确认添加」示例值校验交互一致。
   */
  const handleNextWithValidate = (): void => {
    if (generateYamlLoading) {
      return;
    }
    if (!validateOnNext(value)) {
      return;
    }
    handleNext();
  };

  /** 仅生成 YAML 请求中禁用；必填/格式不在此拦截 */
  const nextDisabled = generateYamlLoading;

  return (
    <>
      {/* Step2 主体：样式根节点见 index.scss */}
      <div className="dbl-model-agent-create-step dbl-model-agent-create-step-field-config">
        <Form>
          <Form.Item
            required={true}
            showStatusIcon={false}
            label={formatMessage({ id: 'dbl_model_agent_create_fields' })}
            status={undefined}
            message=""
          >
            {/* 未勾选任何参与 Cube 的列时，下一步校验失败则加 error 边框 */}
            <div
              className={classNames('dbl-field-config-fields-select-wrap', {
                'dbl-field-config-fields-select-wrap--error':
                  fieldsValueError,
              })}
            >
              <TagFilterSelect
                value={fieldsValue}
                options={fieldOptions}
                listWidth={400}
                maxTags={2}
                overlayClassName="dbl-model-tag-select"
                placeholder={formatMessage({
                  id: 'dbl_model_agent_create_select_fields',
                })}
                onChange={(val) => {
                  handleFieldChange(val);
                  clearFieldsValueError();
                }}
                name={formatMessage({ id: 'dbl_model_agent_create_fields' })}
                tooltip={undefined}
              />
            </div>
          </Form.Item>
        </Form>

        {/* 维度区块 + 指标区块 */}
        <div className="dbl-field-config-split">
          <DimensionSection
            dimRows={dimRows}
            dimColumns={dimColumns}
            dimInlineOpen={dimInlineOpen}
            changeDimInlineOpen={changeDimInlineOpen}
            dimAddDraft={dimAddDraft}
            changeDimAddDraft={changeDimAddDraft}
            dimTypeOptions={dimTypeOptions}
            onConfirmDimInline={handleConfirmDimInline}
            secError={dimSecError}
            allDimSourceColumns={allDimSourceColumns}
            fieldsValue={fieldsValue}
            changeFieldsValue={changeFieldsValue}
          />
          <MeasureSection
            measRows={measRows}
            measColumns={measColumns}
            measInlineOpen={measInlineOpen}
            changeMeasInlineOpen={changeMeasInlineOpen}
            measAddDraft={measAddDraft}
            changeMeasAddDraft={changeMeasAddDraft}
            measTypeOptions={measTypeOptions}
            measFormatOptions={measFormatOptions}
            measGranularityOptions={measGranularityOptions}
            onConfirmMeasInline={handleConfirmMeasInline}
            secError={measSecError}
            allMeasSourceColumns={allMeasSourceColumns}
            fieldsValue={fieldsValue}
            changeFieldsValue={changeFieldsValue}
          />
        </div>
      </div>

      {/* 与步骤主体同级：向导全局 footer 样式由父级页面控制 */}
      <div className="dbl-model-agent-create-footer">
        <Button type="weak" onClick={handlePrev}>
          {formatMessage({ id: 'dbl_model_management_create_model_previous_step' })}
        </Button>
        <Button
          type="primary"
          disabled={nextDisabled}
          loading={generateYamlLoading}
          onClick={handleNextWithValidate}
        >
          {formatMessage({ id: 'dbl_model_management_create_model_next_step' })}
        </Button>
      </div>
    </>
  );
};

export default StepFieldConfig;
